import React, { useEffect, useId, useRef, useState } from 'react';
import './DistributorClinicSettings.css';
import useAccessibleTabs from '../../hooks/useAccessibleTabs';
import { stateCode } from '../../data/usStates';
import DistributorAddressFields from './DistributorAddressFields';
import DistributorClinicUsers from './DistributorClinicUsers';
import {
  CATEGORY_FAMILIES,
  categoryDraft,
  categoryRatesFromDraft,
  sameCategoryRates,
} from '../../services/categoryPricing';

const CUSTOMER_TABS = ['users', 'pricing', 'shipping'];
const TAB_LABELS = { users: 'Users', pricing: 'Pricing', shipping: 'Shipping' };
const ADDRESS = { street: '', city: '', state: '', zipCode: '' };
export const fromDetail = (detail) => ({
  pricingBasis:
    detail.settings.pricingBasis === 'piece' ||
    (detail.settings.pricingBasis === undefined &&
      (Array.isArray(detail.settings.categoryRates) ||
        (detail.settings.categoryRates === undefined && detail.settings.ratePerUnit == null)))
      ? 'piece'
      : 'unit',
  categoryRates: categoryDraft(detail.settings.categoryRates),
  ratePerUnit: detail.settings.ratePerUnit == null ? '' : String(detail.settings.ratePerUnit),
  creditLimit: detail.settings.creditLimit == null ? '' : String(detail.settings.creditLimit),
  orderingEnabled: detail.settings.orderingEnabled !== false,
  allowedProductIds: detail.settings.allowedProductIds,
  deliveryAddress: detail.settings.deliveryAddress
    ? {
        ...detail.settings.deliveryAddress,
        state: stateCode(detail.settings.deliveryAddress.state),
      }
    : { ...ADDRESS },
  defaultPreferredCarrier: detail.settings.defaultPreferredCarrier || 'fedex',
});
const addressKeys = Object.keys(ADDRESS);
export const changed = (baseline, draft) => {
  const changes = {};
  if (
    draft.pricingBasis === 'piece' &&
    (baseline.pricingBasis !== 'piece' ||
      JSON.stringify(baseline.categoryRates) !== JSON.stringify(draft.categoryRates))
  ) {
    changes.categoryRates = categoryRatesFromDraft(draft.categoryRates, { allowZero: true });
  }
  for (const key of [
    'ratePerUnit',
    'creditLimit',
    'orderingEnabled',
    'allowedProductIds',
    'deliveryAddress',
    'defaultPreferredCarrier',
  ]) {
    if (JSON.stringify(baseline[key]) === JSON.stringify(draft[key])) continue;
    if (key === 'ratePerUnit' || key === 'creditLimit')
      changes[key] = draft[key] === '' ? null : Number(draft[key]);
    else if (key === 'deliveryAddress')
      changes[key] = addressKeys.every((field) => !draft.deliveryAddress[field])
        ? null
        : draft.deliveryAddress;
    else changes[key] = draft[key];
  }
  return changes;
};
export const matches = (detail, changes) => {
  const current = fromDetail(detail);
  return Object.entries(changes).every(([key, value]) => {
    if (key === 'categoryRates') return sameCategoryRates(detail.settings.categoryRates, value);
    const currentValue =
      key === 'ratePerUnit' || key === 'creditLimit'
        ? current[key] === ''
          ? null
          : Number(current[key])
        : key === 'deliveryAddress' && addressKeys.every((field) => !current.deliveryAddress[field])
          ? null
          : current[key];
    return JSON.stringify(currentValue) === JSON.stringify(value);
  });
};

export default function DistributorClinicSettings({
  selected,
  workspace = false,
  accountChangesPending = false,
  distributorId,
  onUsersBusyChange,
  usersReadOnly = false,
  draft,
  products,
  saving,
  isSavingSettings = saving,
  readOnly = false,
  unsaved,
  inputClass,
  setField,
  setAddress,
  onSave,
  onDiscard,
  t,
}) {
  const [activeTab, setActiveTab] = useState('users');
  const productModeName = useId();
  const explicitProducts = useRef({ clinic: selected._id, ids: draft.allowedProductIds });
  useEffect(() => {
    if (Array.isArray(draft.allowedProductIds)) {
      explicitProducts.current = { clinic: selected._id, ids: draft.allowedProductIds };
    }
  }, [selected._id, draft.allowedProductIds]);
  const { tabListProps, getTabProps } = useAccessibleTabs({
    tabs: CUSTOMER_TABS,
    activeTab,
    onSelect: setActiveTab,
    label: t('Customer details'),
  });
  const panelProps = (key) =>
    workspace
      ? {
          role: 'tabpanel',
          id: getTabProps(key)['aria-controls'],
          'aria-labelledby': getTabProps(key).id,
          hidden: activeTab !== key,
          inert: activeTab !== key ? '' : undefined,
          tabIndex: 0,
        }
      : {};
  const changes = changed(fromDetail(selected), draft);
  const dirtyTab = (key) =>
    key === 'pricing'
      ? [
          'categoryRates',
          'ratePerUnit',
          'creditLimit',
          'orderingEnabled',
          'allowedProductIds',
        ].some((field) => field in changes)
      : key === 'shipping' &&
        ['deliveryAddress', 'defaultPreferredCarrier'].some((field) => field in changes);
  const revealInvalidField = (event) => {
    const input = event.target;
    const key = input.closest('[data-customer-tab]')?.dataset.customerTab;
    if (!workspace || !key || key === activeTab) return;
    event.preventDefault();
    const firstInvalid = Array.from(
      event.currentTarget.querySelectorAll('input, select, textarea')
    ).find((field) => field.willValidate && !field.validity.valid);
    if (input !== firstInvalid) return;
    setActiveTab(key);
    requestAnimationFrame(() => {
      if (input.isConnected) {
        input.focus();
        input.reportValidity();
      }
    });
  };
  const users = (
    <div {...panelProps('users')} className={workspace ? 'me-dw-customer-users-panel' : undefined}>
      <DistributorClinicUsers
        key={selected._id}
        clinicId={selected._id}
        compact={workspace}
        readOnly={readOnly || usersReadOnly}
        distributorId={distributorId}
        onBusyChange={onUsersBusyChange}
        t={t}
      />
    </div>
  );
  return (
    <section
      aria-label={t('Clinic detail')}
      className={`me-dw-card me-dw-settings ${workspace ? 'me-dw-customer-workspace' : 'space-y-5 p-5 sm:p-6'}`}
    >
      {workspace ? (
        <div {...tabListProps} className="me-dw-customer-tabs">
          {CUSTOMER_TABS.map((key) => (
            <button key={key} type="button" {...getTabProps(key)} onClick={() => setActiveTab(key)}>
              {t(TAB_LABELS[key])}
              {key === 'users' && activeTab !== 'users' && accountChangesPending && (
                <span className="me-dw-tab-draft">{t('Unfinished action')}</span>
              )}
              {key !== 'users' && activeTab !== key && dirtyTab(key) && (
                <span className="me-dw-tab-draft">{t('Unsaved changes')}</span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="me-dw-card-head">
          <h3 className="text-lg font-bold text-[#153d48]">{t('Clinic detail')}</h3>
        </div>
      )}
      {workspace && users}
      <form
        onSubmit={onSave}
        onInvalidCapture={revealInvalidField}
        hidden={workspace && activeTab === 'users'}
        inert={workspace && activeTab === 'users' ? '' : undefined}
        className={workspace ? 'me-dw-customer-form' : 'space-y-4'}
      >
        <div
          {...panelProps('pricing')}
          data-customer-tab="pricing"
          className={workspace ? 'me-dw-customer-settings-panel' : undefined}
        >
          <fieldset
            disabled={saving || readOnly}
            className={workspace ? 'me-dw-customer-fields' : 'space-y-4'}
          >
            <fieldset className="me-dw-customer-group space-y-3">
              <legend className="font-semibold">{t('Pricing & ordering')}</legend>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {draft.pricingBasis === 'piece' ? (
                  CATEGORY_FAMILIES.map(({ familyKey, label }) => (
                    <label key={familyKey}>
                      {t('Retail price per piece for {category}', { category: label })}
                      <input
                        className={inputClass}
                        inputMode="decimal"
                        placeholder={t('Not set')}
                        value={draft.categoryRates[familyKey]}
                        onChange={(event) =>
                          setField('categoryRates', {
                            ...draft.categoryRates,
                            [familyKey]: event.target.value,
                          })
                        }
                      />
                    </label>
                  ))
                ) : (
                  <label>
                    {t('Rate per unit')}
                    <input
                      className={inputClass}
                      type="number"
                      min="0"
                      step="0.0001"
                      value={draft.ratePerUnit}
                      onChange={(event) => setField('ratePerUnit', event.target.value)}
                    />
                  </label>
                )}
                <label>
                  {t('Credit limit')}
                  <input
                    className={inputClass}
                    type="number"
                    min="0"
                    step="0.01"
                    value={draft.creditLimit}
                    onChange={(event) => setField('creditLimit', event.target.value)}
                  />
                </label>
              </div>
              <p className="text-xs text-slate-600">
                {t(
                  draft.pricingBasis === 'piece'
                    ? 'Retail category prices apply per physical piece at every size. They are saved for reporting and never deducted from the distributor bank.'
                    : 'Legacy retail rates use billable units. Credit limits are informational.'
                )}
              </p>
              {draft.pricingBasis !== 'piece' && (
                <button
                  type="button"
                  className="me-dw-link"
                  onClick={() => setField('pricingBasis', 'piece')}
                >
                  {t('Configure retail category prices per piece')}
                </button>
              )}
              {draft.pricingBasis === 'piece' && (
                <p className="text-xs text-slate-600">
                  {t(
                    'Blank retail categories have no configured retail price. Existing orders retain their saved prices.'
                  )}
                </p>
              )}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.orderingEnabled}
                  onChange={(event) => setField('orderingEnabled', event.target.checked)}
                />
                {t('Ordering enabled')}
              </label>
            </fieldset>
            <fieldset className="me-dw-customer-products me-category-product-access space-y-2">
              <legend className="font-medium">{t('Allowed products')}</legend>
              {products === null ? (
                <p className="text-sm text-slate-600">
                  {t('Product list unavailable. Other settings can still be saved.')}
                </p>
              ) : (
                <>
                  {draft.allowedProductIds?.some(
                    (id) => !products.some((product) => product._id === id)
                  ) && (
                    <p className="text-sm text-amber-700">
                      {t(
                        'Some saved products are unavailable. Choose available products or All products available to this distributor to replace this restriction.'
                      )}
                    </p>
                  )}
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={productModeName}
                      checked={draft.allowedProductIds === null}
                      onChange={() => setField('allowedProductIds', null)}
                    />
                    {t('All products available to this distributor')}
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={productModeName}
                      checked={draft.allowedProductIds !== null}
                      disabled={!products.length}
                      onChange={() => {
                        const saved = explicitProducts.current;
                        const ids =
                          saved.clinic === selected._id
                            ? (saved.ids || []).filter((id) =>
                                products.some((product) => product._id === id)
                              )
                            : [];
                        setField(
                          'allowedProductIds',
                          ids.length ? ids : products.map((product) => product._id)
                        );
                      }}
                    />
                    {t('Only selected products')}
                  </label>
                  {draft.allowedProductIds !== null && (
                    <div
                      className="me-dw-product-options flex flex-wrap gap-x-4 gap-y-2"
                      tabIndex={workspace ? 0 : undefined}
                      role={workspace ? 'region' : undefined}
                      aria-label={workspace ? t('Allowed products') : undefined}
                    >
                      {products.map((product) => (
                        <label key={product._id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={draft.allowedProductIds?.includes(product._id) || false}
                            disabled={
                              draft.allowedProductIds?.length === 1 &&
                              draft.allowedProductIds.includes(product._id)
                            }
                            onChange={() => {
                              const ids = (draft.allowedProductIds || []).filter((id) =>
                                products.some((item) => item._id === id)
                              );
                              const next = ids.includes(product._id)
                                ? ids.filter((item) => item !== product._id)
                                : [...ids, product._id];
                              if (next.length) setField('allowedProductIds', next);
                            }}
                          />
                          {product.name}
                        </label>
                      ))}
                    </div>
                  )}
                  {workspace && !readOnly && draft.allowedProductIds?.length === 1 && (
                    <p className="text-xs text-slate-600">
                      {t('Keep at least one product selected, or allow all products.')}
                    </p>
                  )}
                </>
              )}
            </fieldset>
          </fieldset>
        </div>
        <div
          {...panelProps('shipping')}
          data-customer-tab="shipping"
          className={workspace ? 'me-dw-customer-settings-panel' : undefined}
        >
          <fieldset
            disabled={saving || readOnly}
            className={workspace ? 'me-dw-customer-fields' : 'space-y-4'}
          >
            <fieldset className="me-dw-customer-group space-y-3">
              <legend className="font-semibold">{t('Delivery defaults')}</legend>
              <label>
                {t('Default shipper')}
                <select
                  className={inputClass}
                  value={draft.defaultPreferredCarrier}
                  onChange={(event) => setField('defaultPreferredCarrier', event.target.value)}
                >
                  <option value="fedex">FedEx</option>
                  <option value="ups">UPS</option>
                </select>
              </label>
              <DistributorAddressFields
                address={draft.deliveryAddress}
                onChange={setAddress}
                t={t}
                inputClass={inputClass}
              />
            </fieldset>
          </fieldset>
        </div>
        {!readOnly && (
          <div
            className={workspace ? 'me-dw-customer-actions' : 'flex flex-wrap items-center gap-3'}
          >
            <span className="me-dw-customer-save-state">{unsaved && t('Unsaved changes')}</span>
            {unsaved && (
              <button
                type="button"
                disabled={saving}
                onClick={onDiscard}
                className="me-dw-link text-sm"
              >
                {t('Discard changes')}
              </button>
            )}
            <button type="submit" disabled={!unsaved || saving} className="me-dw-primary">
              {t(isSavingSettings ? 'Saving...' : 'Save settings')}
            </button>
          </div>
        )}
      </form>
      {!workspace && users}
    </section>
  );
}
